import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Area, Line, ComposedChart, ReferenceLine, ReferenceArea
} from 'recharts';
import { fetchRangeSummary, fetchShipmentsInsights, fetchShipmentsProjection } from '../../services/shipmentsApi';
import ShipmentsInsights from './ShipmentsInsights';
import ShipmentsProjection from './ShipmentsProjection';
import './ShipmentsGraph.css';

const GREEN = '#46BB89';
const PRODUCT_COLORS = [
  '#63B3ED', '#F6AD55', '#FC8181', '#B794F4',
  '#76E4F7', '#F6E05E', '#FBB6CE', '#FB923C',
  '#A78BFA', '#34D399'
];

const GRANULARITIES = [
  { key: 'month', label: 'Year range',  hint: 'Monthly breakdown' },
  { key: 'week',  label: 'Month range', hint: 'Weekly breakdown' },
  { key: 'day',   label: 'Week range',  hint: 'Daily breakdown' },
];

function currentYear()  { return new Date().getFullYear(); }
function currentMonth() { return new Date().getMonth() + 1; }
function pad(n) { return String(n).padStart(2, '0'); }
function toIsoDate(y, m, d) { return `${y}-${pad(m)}-${pad(d)}`; }
function daysInMonth(y, m) { return new Date(y, m, 0).getDate(); }

function weekStart(date) {
  const d = new Date(date);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  return d;
}

function defaultRange(granularity) {
  const y = currentYear(); const m = currentMonth();
  if (granularity === 'month') return { startYear: y - 1, endYear: y };
  if (granularity === 'week') return { startYear: y, startMonth: Math.max(1, m - 2), endYear: y, endMonth: m };
  const ws = weekStart(new Date());
  const we = new Date(ws); we.setDate(we.getDate() + 6);
  return { startYear: ws.getFullYear(), startMonth: ws.getMonth() + 1, startDay: ws.getDate(),
           endYear: we.getFullYear(), endMonth: we.getMonth() + 1, endDay: we.getDate() };
}

function rangeToFromTo(gran, range) {
  if (gran === 'month') {
    return { from: toIsoDate(range.startYear, 1, 1), to: toIsoDate((range.endYear || currentYear()) + 1, 1, 1) };
  }
  if (gran === 'week') {
    const ey = range.endYear || currentYear(); const em = range.endMonth || currentMonth();
    return { from: toIsoDate(range.startYear, range.startMonth || 1, 1),
             to: toIsoDate(em === 12 ? ey + 1 : ey, em === 12 ? 1 : em + 1, 1) };
  }
  const ey = range.endYear || currentYear(); const em = range.endMonth || currentMonth();
  const ed = range.endDay || daysInMonth(ey, em);
  const toDate = new Date(ey, em - 1, ed + 1);
  return { from: toIsoDate(range.startYear, range.startMonth || 1, range.startDay || 1),
           to: toIsoDate(toDate.getFullYear(), toDate.getMonth() + 1, toDate.getDate()) };
}

function formatLabel(gran, dateStr) {
  if (!dateStr) return '';
  if (gran === 'month') {
    const [y, m] = dateStr.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleString('en-GB', { month: 'short', year: '2-digit' });
  }
  if (gran === 'week') {
    const [yearStr, wStr] = dateStr.split('-W');
    return `W${wStr} '${String(yearStr).slice(2)}`;
  }
  return new Date(dateStr + 'T12:00:00Z').toLocaleString('en-GB', { day: '2-digit', month: 'short' });
}

function CustomTooltip({ active, payload, granularity, topProducts, activeProducts }) {
  if (!active || !payload || !payload[0]) return null;
  const d = payload[0].payload;
  let label = d.date;
  if (granularity === 'month') {
    const [y, m] = d.date.split('-');
    label = new Date(Number(y), Number(m) - 1, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' });
  } else if (granularity === 'week') {
    label = `Week ${d.date.split('-W')[1]}, ${d.date.split('-W')[0]}`;
  } else {
    label = new Date(d.date + 'T12:00:00Z').toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'long' });
  }
  const activePns = topProducts.filter(pn => activeProducts.has(pn));
  const isProjected = d.projected != null;
  return (
    <div className="sg-tooltip">
      <div className="sg-tooltip-label">{label}{isProjected ? ' · forecast' : ''}</div>
      {d.total != null && (
        <div className="sg-tooltip-value">{d.total} <span>total</span></div>
      )}
      {isProjected && (
        <div className="sg-tooltip-value" style={{ color: '#63B3ED' }}>{d.projected} <span style={{ color: 'rgba(255,255,255,0.45)' }}>projected</span></div>
      )}
      {activePns.map((pn, i) => d[pn] != null && d[pn] > 0 && (
        <div key={pn} className="sg-tooltip-pn" style={{ color: PRODUCT_COLORS[i % PRODUCT_COLORS.length] }}>
          {d[pn]} <span style={{ color: 'rgba(255,255,255,0.45)' }}>{pn}</span>
        </div>
      ))}
    </div>
  );
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function ShipmentsGraph() {
  const [granularity, setGranularity]       = useState('month');
  const [range, setRange]                   = useState(() => defaultRange('month'));
  const [points, setPoints]                 = useState([]);
  const [total, setTotal]                   = useState(0);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [showBreakdown, setShowBreakdown]   = useState(false);
  const [topProducts, setTopProducts]       = useState([]);
  const [seriesData, setSeriesData]         = useState({});
  const [activeProducts, setActiveProducts] = useState(new Set());
  const [aiInsights, setAiInsights]         = useState(null);
  const [aiLoading, setAiLoading]           = useState(false);
  const [aiError, setAiError]               = useState(null);
  const [aiStale, setAiStale]               = useState(false);
  const [showProjection, setShowProjection] = useState(false);
  const [projHorizon, setProjHorizon]       = useState(6);
  const [projectedPoints, setProjectedPoints] = useState([]);
  const [projMeta, setProjMeta]             = useState(null);
  const [projLoading, setProjLoading]       = useState(false);
  const [projError, setProjError]           = useState(null);
  const abortRef    = useRef(null);
  const aiAbortRef  = useRef(null);
  const projAbortRef = useRef(null);

  const { from, to } = useMemo(() => rangeToFromTo(granularity, range), [granularity, range]);

  const runAiAnalysis = useCallback((pts, tot, bkd) => {
    if (aiAbortRef.current) aiAbortRef.current.abort();
    const aiController = new AbortController();
    aiAbortRef.current = aiController;
    setAiLoading(true);
    setAiError(null);
    setAiStale(false);
    fetchShipmentsInsights({
      from, to, granularity,
      points: pts,
      total: tot,
      breakdown: bkd || null,
      signal: aiController.signal,
    })
      .then(aiRes => {
        if (aiController.signal.aborted) return;
        if (!aiRes || aiRes.ok === false) throw new Error(aiRes?.error || 'Analysis failed');
        setAiInsights(aiRes.insights);
      })
      .catch(err => {
        if (err?.name === 'AbortError') return;
        setAiError(err.message || 'Could not analyse trend data');
      })
      .finally(() => {
        if (!aiController.signal.aborted) setAiLoading(false);
      });
  }, [from, to, granularity]);

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    if (aiAbortRef.current) aiAbortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;

    setLoading(true);
    setError(null);
    setAiStale(true);

    fetchRangeSummary({ from, to, granularity, breakdown: showBreakdown, signal: controller.signal })
      .then(res => {
        if (cancelled) return;
        if (!res || res.ok === false) throw new Error(res?.error || 'Failed');
        const pts = res.points || [];
        const tot = res.total || 0;
        setPoints(pts);
        setTotal(tot);
        let bkd = null;
        if (showBreakdown && res.breakdown) {
          const pns = res.breakdown.topProducts || [];
          setTopProducts(pns);
          setSeriesData(res.breakdown.series || {});
          setActiveProducts(new Set(pns));
          bkd = res.breakdown;
        } else {
          setTopProducts([]);
          setSeriesData({});
          setActiveProducts(new Set());
        }
        // Trigger AI analysis after data loads
        runAiAnalysis(pts, tot, bkd);
      })
      .catch(err => {
        if (cancelled || err?.name === 'AbortError') return;
        setError(err.message || 'Failed to load data');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => {
      cancelled = true;
      controller.abort();
      if (aiAbortRef.current) aiAbortRef.current.abort();
      if (projAbortRef.current) projAbortRef.current.abort();
      setProjectedPoints([]);
      setProjMeta(null);
    };
  }, [from, to, granularity, showBreakdown, runAiAnalysis]);

  const handleGranularityChange = useCallback(g => {
    setGranularity(g);
    setRange(defaultRange(g));
    setProjectedPoints([]);
    setProjMeta(null);
    setProjHorizon(g === 'month' ? 6 : g === 'week' ? 8 : 14);
  }, []);

  const setRangeField = useCallback((field, value) => {
    setRange(prev => ({ ...prev, [field]: value }));
  }, []);

  const toggleProduct = useCallback(pn => {
    setActiveProducts(prev => {
      const next = new Set(prev);
      if (next.has(pn)) next.delete(pn); else next.add(pn);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setActiveProducts(prev =>
      prev.size === topProducts.length ? new Set() : new Set(topProducts)
    );
  }, [topProducts]);

  const handleRunProjection = useCallback(() => {
    if (points.length === 0 || projLoading) return;
    if (projAbortRef.current) projAbortRef.current.abort();
    const ctrl = new AbortController();
    projAbortRef.current = ctrl;
    setProjLoading(true);
    setProjError(null);
    setProjectedPoints([]);
    setProjMeta(null);
    const bkd = showBreakdown && topProducts.length > 0 ? { topProducts, series: seriesData } : null;
    fetchShipmentsProjection({
      from, to, granularity,
      points,
      total,
      horizonPeriods: projHorizon,
      breakdown: bkd,
      signal: ctrl.signal,
    })
      .then(res => {
        if (ctrl.signal.aborted) return;
        if (!res || res.ok === false) throw new Error(res?.error || 'Projection failed');
        const bands = res.projection.projectedBands || {};
        const proj = Object.entries(res.projection.projectedValues || {})
          .map(([date, projected]) => ({
            date,
            label: formatLabel(granularity, date),
            projected,
            projectedLower: bands[date]?.lower ?? projected,
            projectedUpper: bands[date]?.upper ?? projected,
          }))
          .sort((a, b) => a.date < b.date ? -1 : 1);
        setProjectedPoints(proj);
        setProjMeta(res.projection);
      })
      .catch(err => {
        if (err?.name === 'AbortError') return;
        setProjError(err.message || 'Could not generate projection');
      })
      .finally(() => { if (!ctrl.signal.aborted) setProjLoading(false); });
  }, [from, to, granularity, points, total, projHorizon, showBreakdown, topProducts, seriesData, projLoading]);

  // Merge total points + per-product series + projected points into one data array
  const chartData = useMemo(() => {
    const byDate = {};
    points.forEach(p => {
      byDate[p.date] = { date: p.date, label: formatLabel(granularity, p.date), total: p.count };
    });
    if (showBreakdown) {
      Object.entries(seriesData).forEach(([pn, pts]) => {
        pts.forEach(({ date, count }) => {
          if (!byDate[date]) byDate[date] = { date, label: formatLabel(granularity, date), total: 0 };
          byDate[date][pn] = count;
        });
      });
    }
    projectedPoints.forEach(p => {
      byDate[p.date] = {
        date: p.date, label: p.label,
        projected: p.projected,
        projectedLower: p.projectedLower,
        projectedUpper: p.projectedUpper,
      };
    });
    return Object.values(byDate).sort((a, b) => a.date < b.date ? -1 : 1);
  }, [points, seriesData, showBreakdown, granularity, projectedPoints]);

  const maxCount = useMemo(() => {
    let max = 1;
    chartData.forEach(p => {
      if (p.total > max) max = p.total;
      topProducts.forEach(pn => { if (p[pn] > max) max = p[pn]; });
    });
    chartData.forEach(p => { if ((p.projected || 0) > max) max = p.projected; });
    return max;
  }, [chartData, topProducts]);

  const allActive = activeProducts.size === topProducts.length;

  return (
    <div className="sg-root">
      {/* ── Controls ── */}
      <div className="sg-controls">
        <div className="sg-granularity-tabs">
          {GRANULARITIES.map(g => (
            <button key={g.key} type="button"
              className={`sg-tab${granularity === g.key ? ' active' : ''}`}
              onClick={() => handleGranularityChange(g.key)}>
              {g.label}
              <span className="sg-tab-hint">{g.hint}</span>
            </button>
          ))}
        </div>

        <div className="sg-range-inputs">
          {granularity === 'month' && (<>
            <label className="sg-field"><span>From year</span>
              <input type="number" min="2000" max={currentYear()} value={range.startYear || currentYear()}
                onChange={e => setRangeField('startYear', Number(e.target.value))} />
            </label>
            <span className="sg-range-sep">→</span>
            <label className="sg-field"><span>To year</span>
              <input type="number" min="2000" max={currentYear()} value={range.endYear || currentYear()}
                onChange={e => setRangeField('endYear', Number(e.target.value))} />
            </label>
            <button type="button" className="sg-preset" onClick={() => setRange({ startYear: currentYear(), endYear: currentYear() })}>This year</button>
            <button type="button" className="sg-preset" onClick={() => setRange({ startYear: currentYear() - 1, endYear: currentYear() })}>Last 2 years</button>
            <button type="button" className="sg-preset" onClick={() => setRange({ startYear: currentYear() - 4, endYear: currentYear() })}>Last 5 years</button>
          </>)}

          {granularity === 'week' && (<>
            <label className="sg-field"><span>From</span>
              <div className="sg-month-year">
                <select value={range.startMonth || 1} onChange={e => setRangeField('startMonth', Number(e.target.value))}>
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
                <input type="number" min="2000" max={currentYear()} value={range.startYear || currentYear()}
                  onChange={e => setRangeField('startYear', Number(e.target.value))} />
              </div>
            </label>
            <span className="sg-range-sep">→</span>
            <label className="sg-field"><span>To</span>
              <div className="sg-month-year">
                <select value={range.endMonth || currentMonth()} onChange={e => setRangeField('endMonth', Number(e.target.value))}>
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
                <input type="number" min="2000" max={currentYear()} value={range.endYear || currentYear()}
                  onChange={e => setRangeField('endYear', Number(e.target.value))} />
              </div>
            </label>
            <button type="button" className="sg-preset" onClick={() => { const y = currentYear(); const m = currentMonth(); setRange({ startYear: y, startMonth: Math.max(1, m - 2), endYear: y, endMonth: m }); }}>Last 3 months</button>
            <button type="button" className="sg-preset" onClick={() => { const y = currentYear(); const m = currentMonth(); setRange({ startYear: m <= 6 ? y - 1 : y, startMonth: m <= 6 ? m + 6 : m - 6, endYear: y, endMonth: m }); }}>Last 6 months</button>
          </>)}

          {granularity === 'day' && (<>
            <label className="sg-field"><span>From</span>
              <input type="date" value={toIsoDate(range.startYear, range.startMonth || 1, range.startDay || 1)}
                onChange={e => { const [y,m,d] = e.target.value.split('-').map(Number); setRange(p => ({...p, startYear:y, startMonth:m, startDay:d})); }} />
            </label>
            <span className="sg-range-sep">→</span>
            <label className="sg-field"><span>To</span>
              <input type="date" value={toIsoDate(range.endYear, range.endMonth || 1, range.endDay || 1)}
                onChange={e => { const [y,m,d] = e.target.value.split('-').map(Number); setRange(p => ({...p, endYear:y, endMonth:m, endDay:d})); }} />
            </label>
            <button type="button" className="sg-preset" onClick={() => { const ws = weekStart(new Date()); const we = new Date(ws); we.setDate(we.getDate()+6); setRange({ startYear:ws.getFullYear(), startMonth:ws.getMonth()+1, startDay:ws.getDate(), endYear:we.getFullYear(), endMonth:we.getMonth()+1, endDay:we.getDate() }); }}>This week</button>
            <button type="button" className="sg-preset" onClick={() => { const e=new Date(); const s=new Date(); s.setDate(s.getDate()-13); setRange({ startYear:s.getFullYear(), startMonth:s.getMonth()+1, startDay:s.getDate(), endYear:e.getFullYear(), endMonth:e.getMonth()+1, endDay:e.getDate() }); }}>Last 2 weeks</button>
            <button type="button" className="sg-preset" onClick={() => { const e=new Date(); const s=new Date(); s.setDate(s.getDate()-29); setRange({ startYear:s.getFullYear(), startMonth:s.getMonth()+1, startDay:s.getDate(), endYear:e.getFullYear(), endMonth:e.getMonth()+1, endDay:e.getDate() }); }}>Last 30 days</button>
          </>)}
        </div>
      </div>

      {/* ── Stats row + breakdown toggle ── */}
      <div className="sg-stat-row">
        <div className="sg-stat">
          <span className="sg-stat-num">{loading ? '—' : total.toLocaleString()}</span>
          <span className="sg-stat-label">total devices in range</span>
        </div>
        <div className="sg-stat">
          <span className="sg-stat-num">{loading ? '—' : chartData.length}</span>
          <span className="sg-stat-label">{granularity === 'month' ? 'months' : granularity === 'week' ? 'weeks' : 'days'} with data</span>
        </div>
        {chartData.length > 0 && (
          <div className="sg-stat">
            <span className="sg-stat-num">{loading ? '—' : Math.round(total / Math.max(1, chartData.length))}</span>
            <span className="sg-stat-label">avg per {granularity === 'month' ? 'month' : granularity === 'week' ? 'week' : 'day'}</span>
          </div>
        )}
        {/* Breakdown toggle — lives in the stats row, right-aligned */}
        <button
          type="button"
          className={`sg-breakdown-toggle${showBreakdown ? ' active' : ''}`}
          onClick={() => setShowBreakdown(v => !v)}
          title="Overlay top 10 product numbers"
        >
          <span className="sg-breakdown-icon">⊞</span>
          By product
          {showBreakdown && topProducts.length > 0 && (
            <span className="sg-breakdown-badge">{topProducts.length}</span>
          )}
        </button>
      </div>

      {/* ── Projection controls ── */}
      <div className="sg-projection-row">
        <button
          type="button"
          className={`sg-projection-toggle${showProjection ? ' active' : ''}`}
          onClick={() => setShowProjection(v => !v)}
        >
          <span className="sg-projection-icon">◈</span>
          Project future trend
        </button>
        {showProjection && (
          <>
            <label className="sg-field sg-field--inline">
              <span>{granularity === 'month' ? 'Months' : granularity === 'week' ? 'Weeks' : 'Days'} ahead</span>
              <input
                type="number"
                min="1"
                max={granularity === 'month' ? 36 : granularity === 'week' ? 52 : 90}
                value={projHorizon}
                onChange={e => setProjHorizon(Math.max(1, Number(e.target.value)))}
              />
            </label>
            <button
              type="button"
              className="sg-preset sg-preset--ai"
              onClick={handleRunProjection}
              disabled={projLoading || points.length === 0}
            >
              {projLoading ? '◈ Projecting…' : '◈ Run projection'}
            </button>
            {projectedPoints.length > 0 && (
              <button
                type="button"
                className="sg-preset"
                onClick={() => { setProjectedPoints([]); setProjMeta(null); }}
              >
                Clear
              </button>
            )}
          </>
        )}
      </div>

      {/* ── Product legend (only when breakdown is on) ── */}
      {showBreakdown && topProducts.length > 0 && (
        <div className="sg-legend">
          <button
            type="button"
            className={`sg-legend-toggle-all${allActive ? ' active' : ''}`}
            onClick={toggleAll}
          >
            {allActive ? 'Hide all' : 'Show all'}
          </button>
          <div className="sg-legend-chips">
            {topProducts.map((pn, i) => {
              const color = PRODUCT_COLORS[i % PRODUCT_COLORS.length];
              const isOn = activeProducts.has(pn);
              return (
                <button
                  key={pn}
                  type="button"
                  className={`sg-legend-chip${isOn ? ' active' : ''}`}
                  style={isOn ? { borderColor: color, color, background: `${color}18` } : {}}
                  onClick={() => toggleProduct(pn)}
                >
                  <span className="sg-legend-dot" style={{ background: isOn ? color : 'rgba(255,255,255,0.2)' }} />
                  {pn}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Chart area ── */}
      <div className="sg-chart-area" style={{ minHeight: 340 }}>
        {loading && <div className="sg-loading">Loading…</div>}
        {error && <div className="sg-error">{error}</div>}
        {!loading && !error && chartData.length === 0 && (
          <div className="sg-empty">No production data found for this period.</div>
        )}
        {!loading && !error && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 16, right: 24, left: 0, bottom: 8 }}>
              <defs>
                <linearGradient id="sgGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={GREEN} stopOpacity={showBreakdown ? 0.08 : 0.25} />
                  <stop offset="95%" stopColor={GREEN} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="4 4" vertical={false} />
              <XAxis dataKey="label"
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                tickLine={false} interval="preserveStartEnd" />
              <YAxis allowDecimals={false}
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                axisLine={false} tickLine={false} width={36}
                domain={[0, maxCount + Math.ceil(maxCount * 0.1)]} />
              <Tooltip content={
                <CustomTooltip granularity={granularity} topProducts={topProducts} activeProducts={activeProducts} />
              } cursor={{ stroke: 'rgba(70,187,137,0.3)', strokeWidth: 1 }} />

              {/* Total area — dims when breakdown is on */}
              <Area type="monotone" dataKey="total"
                stroke={showBreakdown ? 'rgba(70,187,137,0.35)' : GREEN}
                strokeWidth={showBreakdown ? 1.5 : 2.5}
                fill="url(#sgGradient)"
                dot={false}
                activeDot={showBreakdown ? false : { fill: GREEN, stroke: '#fff', strokeWidth: 2, r: 5 }}
              />

              {/* Per-product lines — only when breakdown active */}
              {showBreakdown && topProducts.map((pn, i) => {
                if (!activeProducts.has(pn)) return null;
                const color = PRODUCT_COLORS[i % PRODUCT_COLORS.length];
                return (
                  <Line key={pn} type="monotone" dataKey={pn}
                    stroke={color} strokeWidth={2}
                    dot={false}
                    activeDot={{ fill: color, stroke: '#fff', strokeWidth: 2, r: 4 }}
                    connectNulls={false}
                  />
                );
              })}

              {/* Projection: shaded future zone + dashed forecast line */}
              {projectedPoints.length > 0 && (
                <>
                  <ReferenceArea
                    x1={projectedPoints[0].label}
                    x2={projectedPoints[projectedPoints.length - 1].label}
                    fill="rgba(99,179,237,0.04)"
                    stroke="none"
                  />
                  <ReferenceLine
                    x={projectedPoints[0].label}
                    stroke="rgba(99,179,237,0.35)"
                    strokeDasharray="4 3"
                    strokeWidth={1.5}
                    label={{ value: 'Forecast →', position: 'insideTopRight', fill: 'rgba(99,179,237,0.55)', fontSize: 10, dy: -4 }}
                  />
                  {/* Confidence band — ±1σ bounds */}
                  <Line
                    type="monotone"
                    dataKey="projectedUpper"
                    stroke="rgba(99,179,237,0.22)"
                    strokeWidth={1}
                    strokeDasharray="3 4"
                    dot={false}
                    activeDot={false}
                    connectNulls={false}
                    legendType="none"
                  />
                  <Line
                    type="monotone"
                    dataKey="projectedLower"
                    stroke="rgba(99,179,237,0.22)"
                    strokeWidth={1}
                    strokeDasharray="3 4"
                    dot={false}
                    activeDot={false}
                    connectNulls={false}
                    legendType="none"
                  />
                  {/* Central forecast line */}
                  <Line
                    type="monotone"
                    dataKey="projected"
                    stroke="rgba(99,179,237,0.8)"
                    strokeWidth={2.5}
                    strokeDasharray="7 4"
                    dot={false}
                    activeDot={{ fill: '#63B3ED', stroke: 'rgba(99,179,237,0.35)', strokeWidth: 6, r: 4 }}
                    connectNulls={false}
                  />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── AI Insights ── */}
      <ShipmentsInsights
        insights={aiInsights}
        loading={aiLoading}
        error={aiError}
        stale={aiStale}
        onRefresh={() => runAiAnalysis(points, total, showBreakdown && topProducts.length > 0 ? { topProducts, series: seriesData } : null)}
      />

      {/* ── Projection details ── */}
      <ShipmentsProjection
        meta={projMeta}
        loading={projLoading}
        error={projError}
        projectedCount={projectedPoints.length}
        granularity={granularity}
      />
    </div>
  );
}
